import { useState } from 'react';
import { MdDriveFileRenameOutline, MdDelete, MdShare } from 'react-icons/md';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import DOMPurify from 'dompurify';
import ProjectFolderService from '@Helpers/URLHelpers/ProjectFolderService';
import ProjectFilePermission from '@ProjectFunctionalDisplay/ProjectFilePermission';
import ErrorHandler from '@Root/ErrorHandler';
import './ProjectFolderMenu.scss';

export default function ProjectFolderMenu(props) {
    
    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showPermissionDialog, setShowPermissionDialog] = useState(false);
    const [newFolderName, setNewFolderName] = useState(props.folder?.folderName || '');
    const [loading, setLoading] = useState(false);
    const projectFolderService = new ProjectFolderService();
    const errorHandler = new ErrorHandler();
    const className = 'projectFolderMenu';

    const handleClose = () => {
        if (props.onClose) {
            props.onClose();
        }
    };

    const handleRenameClick = () => {
        setNewFolderName(props.folder?.folderName || '');
        setShowRenameDialog(true);
        handleClose();
    };

    const handleRenameSubmit = async () => {
        setLoading(true);
        try {
            const sanitizedName = DOMPurify.sanitize(newFolderName);
            await projectFolderService.updateProjectFolder(props.folderId, {
                folderName: sanitizedName
            });
            setShowRenameDialog(false);
            if (props.renderData) {
                await props.renderData(props.navigationParentId);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const handleDeleteClick = () => {
        setShowDeleteDialog(true);
        handleClose();
    };

    const handleDeleteConfirm = async () => {
        setLoading(true);
        try {
            await projectFolderService.deleteProjectFolder(props.folderId);
            setShowDeleteDialog(false);
            if (props.renderData) {
                await props.renderData(props.navigationParentId);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const handlePermissionClick = () => {
        setShowPermissionDialog(true);
        handleClose();
    };

    return (
        <>
            <Menu
                open={props.open}
                onClose={handleClose}
                anchorEl={props.anchorEl}
                className={className}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        overflow: 'visible',
                        mt: 1,
                        minWidth: 200,
                    },
                }}>
                
                <MenuItem onClick={handleRenameClick} className={className + '__item'}>
                    <ListItemIcon className={className + '__item__icon'}>
                        <MdDriveFileRenameOutline />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Rename"
                        primaryTypographyProps={{
                            className: className + '__item__text'
                        }}
                    />
                </MenuItem>

                <MenuItem onClick={handlePermissionClick} className={className + '__item'}>
                    <ListItemIcon className={className + '__item__icon'}>
                        <MdShare />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Share"
                        primaryTypographyProps={{
                            className: className + '__item__text'
                        }}
                    />
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem onClick={handleDeleteClick} className={className + '__item ' + className + '__item--danger'}>
                    <ListItemIcon className={className + '__item__icon'}>
                        <MdDelete />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Delete"
                        primaryTypographyProps={{
                            className: className + '__item__text'
                        }}
                    />
                </MenuItem>
            </Menu>

            {/* Rename Dialog */}
            <Dialog 
                open={showRenameDialog} 
                onClose={() => setShowRenameDialog(false)}
                maxWidth="sm"
                fullWidth>
                <DialogTitle>Rename Folder</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Folder Name"
                        fullWidth
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        disabled={loading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRenameDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleRenameSubmit} 
                        variant="contained"
                        disabled={!newFolderName || loading}
                        startIcon={loading && <CircularProgress size={16} />}>
                        {loading ? 'Renaming...' : 'Rename'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={showDeleteDialog} 
                onClose={() => setShowDeleteDialog(false)}
                maxWidth="xs"
                fullWidth>
                <DialogTitle>Delete Folder</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{props.folder?.folderName}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        variant="contained"
                        color="error"
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={16} />}>
                        {loading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Permission Dialog */}
            {showPermissionDialog && (
                <ProjectFilePermission
                    open={showPermissionDialog}
                    closePopup={() => setShowPermissionDialog(false)}
                    folderId={props.folderId}
                    navigationParentId={props.navigationParentId}
                    renderData={props.renderData}
                />
            )}
        </>
    );
}